import React, { useEffect, useRef, useState } from 'react';
import { X, Mail, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import '../styles/staffing-theme.css';
import './EarlyAccessModal.css';

/**
 * EarlyAccessModal — Phase 14.
 *
 * Waitlist modal fired by the TopNav "Log In" / "Sign Up" buttons. No real
 * auth yet (that ships with Stripe); the modal just captures email + source
 * via POST /api/leads. Uses two visually-differentiated internal tabs so the
 * user's intent is preserved in the `source` field (login vs signup).
 *
 * Success state stays for a beat before allowing the user to close, so the
 * confirmation is obvious. Duplicate emails also succeed silently — the
 * backend handles that via a unique index + soft error catch.
 */

const API = process.env.REACT_APP_BACKEND_URL;
// Lightweight client-side email check — the real validation is the Pydantic
// EmailStr on the backend, which returns 422 for anything malformed.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EarlyAccessModal({ isOpen, source, referrerPath, onClose }) {
  const [tab, setTab] = useState(source || 'signup');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');   // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef(null);

  // Sync tab when parent tells us "signup" or "login" — different buttons
  // reuse the same modal instance.
  useEffect(() => {
    if (isOpen && source) setTab(source);
  }, [isOpen, source]);

  // Reset form on open. Do NOT clear on close, so a user who accidentally
  // dismisses can reopen and retry.
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setErrorMsg('');
      // Give the modal a paint frame before we grab focus — otherwise iOS
      // Safari fails to auto-scroll into view.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Escape-to-close, only when the modal is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          source: tab,
          referrer_path: referrerPath || null,
        }),
      });
      if (!res.ok) {
        // Pydantic EmailStr → 422 for invalid. Any other 4xx/5xx is
        // treated as a transient error the user can retry.
        const body = await res.json().catch(() => ({}));
        const friendly = res.status === 422
          ? 'That doesn\'t look like a valid email — try again.'
          : 'Something went wrong. Please try again in a moment.';
        setStatus('error');
        setErrorMsg(body?.detail?.[0]?.msg || friendly);
        return;
      }
      setStatus('success');
      // Phase 15 (AUTHENTICATION_STATE, 2/4) — persist a mock auth session so
      // TopNav flips to the logged-in state. Real auth ships with Stripe;
      // key name `sv_user` is the standardized project auth key.
      try {
        localStorage.setItem('sv_user', JSON.stringify({ email: trimmed, ts: Date.now() }));
        window.dispatchEvent(new Event('sv-auth:changed'));
      } catch { /* noop */ }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and retry.');
    }
  };

  const stopClickInside = (e) => e.stopPropagation();
  const isSignup = tab === 'signup';

  return (
    <div
      className="ea-backdrop"
      onClick={onClose}
      onMouseDown={onClose}
      role="presentation"
      data-testid="ea-backdrop"
    >
      <div
        className="ea-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ea-title"
        data-testid="early-access-modal"
        onClick={stopClickInside}
        onMouseDown={stopClickInside}
      >
        <button
          type="button"
          className="ea-close"
          aria-label="Close"
          onClick={onClose}
          data-testid="ea-close"
        ><X size={16} /></button>

        <h2 id="ea-title" className="ea-title">
          <span className="ea-rocket" aria-hidden="true">🚀</span>
          Early Access
        </h2>

        {status === 'success' ? (
          <div className="ea-success" data-testid="ea-success">
            <CheckCircle2 className="ea-success-icon" size={44} strokeWidth={1.8} />
            <p className="ea-success-title">You&apos;re on the list.</p>
            <p className="ea-success-copy">
              We&apos;ll email <strong>{email}</strong> the moment Business accounts open.
            </p>
            <button
              type="button"
              className="ea-btn ea-btn--ghost"
              onClick={onClose}
              data-testid="ea-success-close"
            >Got it</button>
          </div>
        ) : (
          <>
            <div className="ea-tabs" role="tablist" aria-label="Auth intent">
              <button
                type="button"
                role="tab"
                aria-selected={!isSignup}
                className={`ea-tab ${!isSignup ? 'ea-tab--active' : ''}`}
                onClick={() => setTab('login')}
                data-testid="ea-tab-login"
              >Log In</button>
              <button
                type="button"
                role="tab"
                aria-selected={isSignup}
                className={`ea-tab ${isSignup ? 'ea-tab--active' : ''}`}
                onClick={() => setTab('signup')}
                data-testid="ea-tab-signup"
              >Sign Up</button>
            </div>

            <p className="ea-copy" data-testid="ea-copy">
              Business subscriptions launching{' '}
              <strong>Q1 2026</strong>. Reserve your spot — we&apos;ll notify you
              the moment accounts open.
            </p>

            <form className="ea-form" onSubmit={submit} noValidate>
              <div className="ea-input-wrap">
                <Mail className="ea-input-icon" size={16} strokeWidth={2} aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="your.email@company.com"
                  className="ea-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'loading'}
                  maxLength={200}
                  data-testid="ea-email-input"
                  aria-invalid={status === 'error'}
                  aria-describedby={status === 'error' ? 'ea-err' : undefined}
                />
              </div>

              {status === 'error' && (
                <p id="ea-err" className="ea-error" role="alert" data-testid="ea-error">
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                className="ea-btn ea-btn--primary"
                disabled={status === 'loading' || !email.trim()}
                data-testid="ea-submit"
              >
                {status === 'loading' ? (
                  <><Loader2 className="ea-spin" size={16} /> Sending…</>
                ) : (
                  <>{isSignup ? 'Get Early Access' : 'Join Waitlist'} <ArrowRight size={14} /></>
                )}
              </button>
            </form>

            <p className="ea-footnote">
              We&apos;ll only use this to notify you about launch. No spam.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
